import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { Row } from "views/applications-id-report/components/Row";
import { useState } from "react";
import { Property } from "types/landlord/address";
import { SectionCard } from "shared/app/SectionCard";
import { formatPhone } from "shared/utils";
import { BrandingForm } from "shared/app/BrandingForm";
import { useFormTyped } from "shared/hooks";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import { getErrorMessage } from "utils/error";
import * as yup from "yup";

const sessionKey = `${Date.now()}-${crypto.randomUUID()}`;

interface EditBrandingFormProps {
  onClose: () => void;
  propertyId: number;
  initialValues: {
    logo?: string;
    primary_color?: string;
    landlord_name?: string;
    landlord_phone?: string;
    palette?: Record<string, string>;
  };
}

const brandingValidationSchema = yup.object({
  logo: yup.string().optional(),
  primary_color: yup.string().optional(),
  landlord_name: yup
    .string()
    .max(100, "Must be at most 100 characters")
    .optional(),
  landlord_phone: yup
    .string()
    .test(
      "phone-length",
      "Phone number must be exactly 10 characters",
      (value) => !value || value.length === 10
    )
    .optional(),
  palette: yup.object().optional()
});

const EditBrandingForm = ({
  onClose,
  propertyId,
  initialValues
}: EditBrandingFormProps) => {
  const queryClient = useQueryClient();
  const [isLogoUploading, setIsLogoUploading] = useState(false);

  const { register, onSubmit } = useFormTyped(
    initialValues,
    brandingValidationSchema
  );

  const { mutate: updateBranding, isPending } = useMutation({
    mutationFn: (values: EditBrandingFormProps["initialValues"]) => {
      return addressApi.editProperty({
        propertyId,
        branding: {
          logo: values.logo,
          primary_color: values.primary_color,
          landlord_name: values.landlord_name,
          landlord_phone: values.landlord_phone,
          palette: values.palette
        }
      });
    },
    onSuccess: () => {
      toast.success("Branding updated successfully");
      queryClient.invalidateQueries({
        queryKey: ["properties", String(propertyId)]
      });
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to update branding: ${getErrorMessage(error)}`);
    }
  });

  const handleSubmit = onSubmit({
    onSuccess: (values) => {
      updateBranding(values);
      onClose();
    },
    onError: () => {
      toast.error("Form validation failed");
    }
  });

  return (
    <BrandingForm
      register={register}
      sessionKey={sessionKey}
      onUploadingChange={setIsLogoUploading}
      footer={
        <SaveCancelButtons
          onSave={(e) => handleSubmit(e)}
          onCancel={onClose}
          saveLoading={isPending}
          saveDisabled={isLogoUploading}
        />
      }
    />
  );
};

export const BrandingSection = ({ property }: { property: Property }) => {
  const [isEditing, setIsEditing] = useState(false);

  if (isEditing)
    return (
      <EditBrandingForm
        onClose={() => setIsEditing(false)}
        propertyId={property.id}
        initialValues={{
          logo: property.branding?.logo?.asset?.url || property.branding?.logo,
          primary_color: property.branding?.primary_color,
          landlord_name: property.branding?.landlord_name,
          landlord_phone: property.branding?.landlord_phone,
          palette: property.branding?.palette
        }}
      />
    );

  return (
    <SectionCard
      title="Branding"
      headerExtra={
        <Button
          size={ButtonSize.SM}
          variant={ButtonVariant.BASE}
          onClick={() => setIsEditing(true)}
        >
          Edit
        </Button>
      }
    >
      <div className="space-y-4">
        {property.branding?.logo ? (
          <img
            src={property.branding.logo.asset?.url || property.branding.logo}
            alt="Logo"
            className="h-14"
          />
        ) : null}

        <Row title="Primary color" value={property.branding?.primary_color} />
        <Row title="Landlord name" value={property.branding?.landlord_name} />
        <Row
          title="Landlord phone"
          value={formatPhone(property.branding?.landlord_phone || "")}
        />
      </div>
    </SectionCard>
  );
};
